import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CopyOutlined } from '@ant-design/icons';
import { App, Button, Col, Form, Input, Modal, Row, Space, Spin, Tooltip } from 'antd';
import { Store } from 'antd/es/form/interface';

import { isEmpty } from 'lodash';

import { AppDispatch, RootState } from '../../../store';
import { SaveSpokeAccountRequest } from '../models';
import * as actions from '../store/actions';

import './../styles.less';

export type SpokeAccountEditProps = {
  showModal: boolean;
  onCloseModal: () => void;
  resetAndRefresh: () => void;
};

const SpokeAccountEdit = ({ showModal, onCloseModal, resetAndRefresh }: SpokeAccountEditProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const { modal } = App.useApp();

  const [formRef] = Form.useForm<SaveSpokeAccountRequest>();

  const [isModified, setIsModified] = useState<boolean>(false);
  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);

  const { spokeAccount, spokeAccountLoading, webhookUrl, webhookUrlLoading, saveSpokeAccountLoading } = useSelector(
    (state: RootState) => state.spoke,
  );

  useEffect(() => {
    if (!didMount.current) {
      Promise.all([dispatch(actions.getWebhookUrl()), dispatch(actions.getSpokeAccount())]).then(() => {
        setInitiatingDone(true);
        onResetClick();
      });

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    onResetClick();
  }, [spokeAccount, spokeAccountLoading]);

  const onClose = (isModifiedModal: boolean) => {
    if (isModifiedModal) {
      modal.confirm({
        title: 'All changes will be lost. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          onResetClick();
          onCloseModal?.();
        },
      });
    } else {
      onResetClick();
      resetAndRefresh?.();
      onCloseModal?.();
    }
  };

  const onFinish = () => {
    formRef.validateFields().then((formData) => {
      dispatch(actions.saveSpokeAccount(formData)).then(() => {
        dispatch(actions.getMessageControlParameters()).then(() => {
          modal.success({
            title: 'Your settings are saved successfully',
            onOk: () => {
              setIsModified(false);
              onClose(false);
            },
          });

          modal.success({
            title: (
              <React.Fragment>
                <p>
                  Please check the settings at account.spokephone.com for the webhook. Without a configured webhook, you will not be able to receive
                  incoming messages. To set up a webhook, go to account.spokephone.com, then Developers &gt; Webhooks &gt; Add webhook/Edit existing
                  webhook.
                </p>
                <p>- Copy the URL from the NowCerts Spoke settings modal and paste it to the URL field on account.spokephone.com.</p>
                <p>- Select the 'conversation.message.created' from the Subscribed Events.</p>
                <p>- Mark the 'Enabled'.</p>
                <p>- Type any text into the Description field.</p>
                <p>- Save</p>
              </React.Fragment>
            ),
          });
        });
      });
    });
  };

  const onResetClick = () => {
    formRef.resetFields();
    setIsModified(false);
  };

  const onDeleteClick = () => {
    if (!spokeAccount || !spokeAccount.id) {
      return;
    }

    modal.confirm({
      title: 'Are you sure you want to delete?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        return new Promise((resolve, reject) => {
          dispatch(
            actions.deleteSpokeAccount({
              spokeAccountId: spokeAccount.id || '',
            }),
          )
            .then(() => {
              return dispatch(actions.getMessageControlParameters()).then(() => {
                modal.success({
                  title: 'Your settings are saved successfully',
                  onOk: () => {
                    setIsModified(false);
                    onClose(false);
                    resolve(undefined);
                  },
                });
              });
            })
            .catch(reject);
        });
      },
    });
  };

  return (
    <Modal
      title="Spoke account settings"
      open={showModal}
      footer={null}
      onCancel={() => onClose(isModified)}
      onClose={() => onClose(isModified)}
      width={600}
    >
      <div className="account-modal-settings">
        <Spin spinning={spokeAccountLoading || !initiatingDone}>
          <Form
            form={formRef}
            size="small"
            className="certificate-holders-filter-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ ...spokeAccount, webhookUrl } as Store}
            validateMessages={{
              // eslint-disable-next-line no-template-curly-in-string
              required: '${label} is required!',
              types: {
                email: 'It is not a valid email!',
              },
            }}
            onFinish={onFinish}
            onChange={() => (isModified ? null : setIsModified(true))}
          >
            <Form.Item hidden name="id">
              <Input type="hidden" />
            </Form.Item>

            <Tooltip title="User's email address (as registered in Spoke).">
              <Form.Item label="E-Mail" name="email" rules={[{ type: 'email', required: true }]}>
                <Input type="email" />
              </Form.Item>
            </Tooltip>

            <Tooltip title="The client id from the Developer API.">
              <Form.Item
                label="Client ID"
                name="clientId"
                rules={[
                  { type: 'string', required: true },
                  {
                    pattern: new RegExp(/^\S*$/),
                    message: 'The Client ID field must not contain an empty character.',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Tooltip>

            <Tooltip title="The client secret from the Developer API.">
              <Form.Item
                label="Client Secret"
                name="clientSecret"
                rules={[
                  { type: 'string', required: true },
                  {
                    pattern: new RegExp(/^\S*$/),
                    message: 'The Client secret field must not contain an empty character.',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Tooltip>

            <Tooltip title="DID phone numbers assigned to this user (Direct-in-dial). It is on account.spokephone.com. Users > Manage Users > Edit Action > DID phone.">
              <Form.Item
                label="DID Phone"
                name="didPhone"
                rules={[
                  {
                    type: 'string',
                    required: true,
                  },
                  {
                    pattern: new RegExp(/\+\d\d\d\d\d\d\d\d\d\d\d/i),
                    message: "The DID Phone must be in format '+12345678912'.",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Tooltip>

            <Tooltip title="This is the URL for Spoke Webhook system. With this URL Spoke can give us the information about incoming messages to the Spoke system. With this information we can show you new incoming messages without refresh the page.">
              <Spin spinning={webhookUrlLoading}>
                <Form.Item label="Webhook URL" name="webhookUrl" rules={[{ type: 'url' }]}>
                  <Input.Group compact>
                    <Input type="url" disabled={true} value={webhookUrl ?? ''} style={{ width: 'calc(100% - 32px)' }} />
                    <Button icon={<CopyOutlined />} onClick={() => webhookUrl && navigator.clipboard.writeText(webhookUrl)} disabled={!webhookUrl} />
                  </Input.Group>
                </Form.Item>
              </Spin>
            </Tooltip>

            <Row>
              <Col span={24} className="modal-form-control-buttons">
                <Space>
                  <Button type="primary" htmlType="submit" loading={saveSpokeAccountLoading}>
                    {!isEmpty(spokeAccount?.id) ? 'Update' : 'Create'}
                  </Button>

                  <Tooltip title="Reset all changes to the unchanged state">
                    <Button htmlType="button" onClick={onResetClick}>
                      Reset
                    </Button>
                  </Tooltip>

                  {!isEmpty(spokeAccount?.id) ? (
                    <Button type="primary" danger loading={saveSpokeAccountLoading} onClick={onDeleteClick}>
                      Delete
                    </Button>
                  ) : undefined}

                  <Button onClick={() => onClose(isModified)}>Cancel</Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Spin>
      </div>
    </Modal>
  );
};

export default SpokeAccountEdit;
